/**
 * Client-Side Media Optimizer for Swarna CRM CMS
 * 
 * 1. compressImage: Automatically compresses heavy DSLR/phone photos (10-25MB)
 *    to crisp ~150KB-300KB WebP/JPEG using HTML5 Canvas before uploading.
 * 2. generateVideoThumbnail: Captures a crisp poster frame from a video file at 1s.
 * 3. formatFileSize: Human-readable bytes formatter.
 */

export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 to 1.0 (default: 0.82)
  targetFormat?: 'image/jpeg' | 'image/webp' | 'image/png';
}

/**
 * Compresses an image file in the browser using HTML5 Canvas.
 */
export async function compressImage(
  file: File,
  options: ImageCompressionOptions = {}
): Promise<File> {
  // If not an image or SVG/GIF, return as-is
  if (!file.type.startsWith('image/') || file.type.includes('svg') || file.type.includes('gif')) {
    return file;
  }

  const maxWidth = options.maxWidth || 1920;
  const maxHeight = options.maxHeight || 1920;
  const quality = options.quality !== undefined ? options.quality : 0.82;
  const format = options.targetFormat || 'image/jpeg';

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions preserving aspect ratio
        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file);
          return;
        }

        // Smooth image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }

            // Only use compressed if it's smaller than original
            if (blob.size < file.size) {
              const ext = format === 'image/webp' ? 'webp' : 'jpg';
              const cleanName = file.name.replace(/\.[^/.]+$/, '') + `_opt.${ext}`;
              const optimizedFile = new File([blob], cleanName, {
                type: format,
                lastModified: Date.now(),
              });
              resolve(optimizedFile);
            } else {
              resolve(file);
            }
          },
          format,
          quality
        );
      };

      img.onerror = () => resolve(file);
    };

    reader.onerror = () => resolve(file);
  });
}

/**
 * Extracts a frame from a video file to use as a cover poster image.
 */
export async function generateVideoThumbnail(
  videoFile: File,
  seekTimeSec: number = 1.0
): Promise<{ file: File; dataUrl: string } | null> {
  return new Promise((resolve) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const url = URL.createObjectURL(videoFile);
      video.src = url;

      video.onloadedmetadata = () => {
        // Seek to safe time within video length
        video.currentTime = Math.min(seekTimeSec, Math.max(0.1, video.duration / 2));
      };

      video.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 1080;
        canvas.height = video.videoHeight || 1920;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          resolve(null);
          return;
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) {
            resolve(null);
            return;
          }

          const thumbName = `${videoFile.name.replace(/\.[^/.]+$/, '')}_poster.jpg`;
          const thumbFile = new File([blob], thumbName, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          resolve({ file: thumbFile, dataUrl });
        }, 'image/jpeg', 0.85);
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
    } catch {
      resolve(null);
    }
  });
}

/**
 * Detects whether a video file is portrait (vertical 9:16) or landscape (horizontal 16:9).
 */
export async function detectVideoAspectRatio(videoFile: File): Promise<'portrait' | 'landscape'> {
  return new Promise((resolve) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;

      const url = URL.createObjectURL(videoFile);
      video.src = url;

      video.onloadedmetadata = () => {
        const width = video.videoWidth || 1080;
        const height = video.videoHeight || 1920;
        URL.revokeObjectURL(url);
        // If width is greater than height, it is landscape (16:9)
        if (width > height) {
          resolve('landscape');
        } else {
          resolve('portrait');
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve('portrait');
      };
    } catch {
      resolve('portrait');
    }
  });
}

/**
 * Human readable file size formatter.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
